import { useEffect, useRef, useState } from "react";
import { usePushSubscription } from "@/hooks/usePushSubscription";
import { useAuth } from "@/hooks/useAuth";
import { Bell, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/lib/notify";

const PUSH_DISMISSED_KEY = "nitfisio_push_prompt_dismissed";

/**
 * Componente que gerencia a inscrição de push notifications.
 * Exibe um banner discreto na primeira vez que o usuário loga,
 * solicitando permissão para ativar notificações push.
 */
export function PushSubscriptionManager() {
  const { user } = useAuth();
  const { isSupported, isSubscribed, permission, subscribe } = usePushSubscription();
  const [showBanner, setShowBanner] = useState(false);
  const [activating, setActivating] = useState(false);
  const promptedRef = useRef(false);

  useEffect(() => {
    if (!user || !isSupported || isSubscribed || promptedRef.current) return;
    if (permission === "denied") return;

    // Already granted but not subscribed — auto-subscribe silently
    if (permission === "granted") {
      subscribe();
      return;
    }

    // Check if user previously dismissed the prompt
    const dismissed = localStorage.getItem(`${PUSH_DISMISSED_KEY}_${user.id}`);
    if (dismissed) return;

    // Show banner after a short delay so user isn't overwhelmed on login
    const timer = setTimeout(() => {
      promptedRef.current = true;
      setShowBanner(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, [user, isSupported, isSubscribed, permission, subscribe]);

  // Auto-hide banner once subscribed
  useEffect(() => {
    if (isSubscribed && showBanner) setShowBanner(false);
  }, [isSubscribed, showBanner]);

  const handleActivate = async () => {
    setActivating(true);
    const success = await subscribe();
    setActivating(false);
    if (success) {
      toast.success("Notificações push ativadas com sucesso!");
      setShowBanner(false);
    } else {
      toast.error("Não foi possível ativar as notificações. Verifique as permissões do navegador.");
    }
  };

  const handleDismiss = () => {
    if (user) {
      localStorage.setItem(`${PUSH_DISMISSED_KEY}_${user.id}`, "true");
    }
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 mx-auto max-w-md animate-in slide-in-from-bottom-4 fade-in duration-300">
      <div className="rounded-xl border bg-card p-4 shadow-lg">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
            <Bell className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 space-y-1">
            <p className="text-sm font-medium">Ativar notificações</p>
            <p className="text-xs text-muted-foreground">
              Receba alertas de agendamentos, mensagens e atualizações em tempo real.
            </p>
          </div>
          <button
            onClick={handleDismiss}
            className="shrink-0 rounded-md p-1 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Fechar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-3 flex gap-2 justify-end">
          <Button variant="ghost" size="sm" onClick={handleDismiss}>
            Agora não
          </Button>
          <Button size="sm" onClick={handleActivate} disabled={activating}>
            {activating ? "Ativando..." : "Ativar"}
          </Button>
        </div>
      </div>
    </div>
  );
}
